import React, { useEffect, useRef, useState } from 'react';
import {
  X, Upload, UserCheck, CheckCircle, XCircle, Clock,
  Zap, AlertTriangle, FileText, Link2, RefreshCw, MessageSquare,
} from 'lucide-react';
import { invoicesAPI } from '../api/client';

// ─── Event type config ────────────────────────────────────────────────────────
const typeConfig = {
  UPLOADED:      { icon: Upload,       color: 'text-blue-600',    bg: 'bg-blue-50',    dot: 'bg-blue-500',    label: 'Uploaded'           },
  ASSIGNED:      { icon: UserCheck,    color: 'text-indigo-600',  bg: 'bg-indigo-50',  dot: 'bg-indigo-400',  label: 'Assigned'           },
  APPROVED:      { icon: CheckCircle,  color: 'text-green-600',   bg: 'bg-green-50',   dot: 'bg-green-500',   label: 'Approved'           },
  REJECTED:      { icon: XCircle,      color: 'text-red-600',     bg: 'bg-red-50',     dot: 'bg-red-500',     label: 'Rejected'           },
  PENDING:       { icon: Clock,        color: 'text-amber-600',   bg: 'bg-amber-50',   dot: 'bg-amber-400',   label: 'Pending'            },
  CANCELLED:     { icon: XCircle,      color: 'text-gray-500',    bg: 'bg-gray-50',    dot: 'bg-gray-400',    label: 'Cancelled'          },
  POSTED:        { icon: Zap,          color: 'text-purple-600',  bg: 'bg-purple-50',  dot: 'bg-purple-500',  label: 'Posted to ERP'      },
  POST_FAILED:   { icon: AlertTriangle,color: 'text-red-600',     bg: 'bg-red-50',     dot: 'bg-red-400',     label: 'ERP Post Failed'    },
  REVERT_POSTING:{ icon: RefreshCw,    color: 'text-orange-600',  bg: 'bg-orange-50',  dot: 'bg-orange-400',  label: 'ERP Post Reverted'  },
  SPLIT:         { icon: FileText,     color: 'text-cyan-600',    bg: 'bg-cyan-50',    dot: 'bg-cyan-500',    label: 'Split'              },
  MATCH:         { icon: Link2,        color: 'text-teal-600',    bg: 'bg-teal-50',    dot: 'bg-teal-500',    label: 'PO Matched'         },
  ACCRUAL:       { icon: FileText,     color: 'text-sky-600',     bg: 'bg-sky-50',     dot: 'bg-sky-500',     label: 'Accrual'            },
  DEFAULT:       { icon: FileText,     color: 'text-gray-600',    bg: 'bg-gray-50',    dot: 'bg-gray-400',    label: ''                   },
};

function getConfig(type) {
  // Try exact match, then prefix match
  if (typeConfig[type]) return typeConfig[type];
  for (const key of Object.keys(typeConfig)) {
    if (type?.startsWith(key)) return typeConfig[key];
  }
  return typeConfig.DEFAULT;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

// ─── Single timeline event ────────────────────────────────────────────────────
function TimelineEvent({ event, isLast }) {
  const cfg = getConfig(event.type);
  const Icon = cfg.icon;

  return (
    <div className="flex gap-3">
      {/* Vertical line + dot */}
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.bg} border-2 border-white shadow-sm`}>
          <Icon className={`w-4 h-4 ${cfg.color}`} />
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
      </div>

      {/* Content */}
      <div className={`flex-1 pb-5 ${isLast ? '' : ''}`}>
        <div className={`rounded-lg border p-3 ${cfg.bg} border-gray-200`}>
          {/* Header row */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className={`text-sm font-semibold ${cfg.color}`}>{event.label}</span>
              {event.meta && (
                <span className="ml-2 text-xs text-gray-500">{event.meta}</span>
              )}
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
              {formatDate(event.at)}
            </span>
          </div>

          {/* By line */}
          {event.by && event.by !== 'System' && (
            <p className="mt-1 text-xs text-gray-500">
              by <span className="font-medium text-gray-700">{event.by}</span>
            </p>
          )}

          {/* Comment block */}
          {event.comment && (
            <div className="mt-2 flex gap-1.5 bg-white border border-blue-200 rounded px-3 py-2">
              <MessageSquare className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700 italic leading-snug">{event.comment}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
/**
 * InvoiceHistoryModal — full document timeline as a centred fixed modal.
 *
 * Props:
 *   invoiceId  {string}   Invoice UUID
 *   onClose    {function} Called when the modal should close
 */
export default function InvoiceHistoryModal({ invoiceId, onClose }) {
  const [events, setEvents] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    invoicesAPI.getHistory(invoiceId)
      .then(res => {
        if (cancelled) return;
        setEvents(res.data?.events || []);
        setInvoiceNumber(res.data?.invoiceNumber || '');
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load history.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [invoiceId]);

  // Close on backdrop click
  function handleBackdropClick(e) {
    if (e.target === backdropRef.current) onClose();
  }

  // Close on Escape
  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    /* Fixed backdrop — sits above everything */
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Invoice history"
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            <div>
              <h2 className="text-base font-semibold text-gray-800">Document History</h2>
              {invoiceNumber && (
                <p className="text-xs text-gray-500">Invoice #{invoiceNumber}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-sm text-gray-500">Loading history…</span>
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600 text-center py-8">{error}</p>
          )}
          {!loading && !error && events.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">No history available.</p>
          )}
          {!loading && !error && events.length > 0 && (
            <div className="pt-1">
              {events.map((ev, i) => (
                <TimelineEvent key={i} event={ev} isLast={i === events.length - 1} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
